import * as Yup from 'yup';
import { Checkbox } from 'src/components/ui/checkbox';
import { DealerManagementService } from 'src/services/dealer-management.service';
import Header from './components/header';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import Spinner from 'src/components/ui/spinner';
import TooltipIcon from 'src/components/ui/customTooltip';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL,
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM,
    ADD_NEW_USER_MODAL_TITLE,
    EDIT_USER_MODAL_CONTENT,
} from 'src/localization/resouceKeys';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { useCallback, useEffect, useState } from 'react';
import './styles.scss';

const validationSchema = Yup.object({
    targetDealerId: Yup.string()
        .trim()
        .required('This field is required')
        .matches(/^[A-Z0-9]{6}$/, 'Missing or incomplete search criteria.'),
});

const DealerGroupMove = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [status, setStatus] = useState<true | false | null>(null);
    const [searchedDealerId, setSearchedDealerId] = useState();
    const [dealerGroupData, setDealerGroupData] = useState();

    useEffect(() => {
        const cachedData = sessionStorage.getItem('dealerGroupState');
        if (cachedData) {
            const { dealerGroups, dealerGroupSearch } = JSON.parse(cachedData);
            setSearchedDealerId(dealerGroupSearch);
            setDealerGroupData(dealerGroups);
        }
    }, []);

    if (!state?.dealerIdToMove) {
        return (
            <Navigate
                to='/dashboard/systemadmin/dealer-group-maintenance/search'
                replace
            />
        );
    }

    const handleActiveModal = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: t(ADD_NEW_USER_MODAL_TITLE),
                bodyContent: t(EDIT_USER_MODAL_CONTENT),
                cancelBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL),
                confirmBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM),
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    useEffect(() => {
        handleActiveModal();
    }, []);

    const headerBackClickHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelhandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    const cancelhandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: { shouldShow: false },
        });
    };

    const confirmHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
        });
        navigate('/dashboard/systemadmin/dealer-group-maintenance/search', {
            state: {
                dealerGroups: dealerGroupData,
                dealerGroupSearch: searchedDealerId,
            },
            replace: true,
        });
    };

    const formik = useFormik({
        initialValues: {
            targetDealerId: '',
            preserve: true,
        },
        validationSchema: validationSchema,
        validateOnChange: true,
        onSubmit: async (values) => {
            const service = new DealerManagementService();
            try {
                setIsLoading(true);
                setStatus(null);

                await service.moveDealerToGroup(
                    state?.dealerIdToMove,
                    values.targetDealerId,
                    values.preserve,
                );

                if (searchedDealerId) {
                    try {
                        const res = await service.getDealerGroups(
                            searchedDealerId,
                        );
                        if (res.data) {
                            const dealersGroupState = {
                                dealerGroups: res.data,
                                dealerGroupSearch: searchedDealerId,
                                isSuccess: true,
                            };
                            sessionStorage.setItem(
                                'dealerGroupState',
                                JSON.stringify(dealersGroupState),
                            );
                            navigate(
                                '/dashboard/systemadmin/dealer-group-maintenance/search',
                                {
                                    state: dealersGroupState,
                                    replace: true,
                                },
                            );
                        }
                    } catch {
                        navigate(
                            '/dashboard/systemadmin/dealer-group-maintenance/search',
                            {
                                state: {
                                    ...state,
                                    isSuccess: true,
                                },
                                replace: true,
                            },
                        );
                    }
                }
            } catch {
                setStatus(false);
            } finally {
                setIsLoading(false);
            }
        },
    });

    const {
        values,
        setFieldValue,
        isValid,
        dirty,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        isSubmitting,
    } = formik;

    const onPreserveCheck = useCallback(
        (e: any) => {
            setFieldValue('preserve', e);
        },
        [setFieldValue],
    );

    return (
        <div className={'edit-user-container'}>
            <Header backClickHandler={headerBackClickHandler} />
            <div className='sys-admin-user-migration-body move-dealer u-mt-xlarge'>
                {status !== null ? (
                    <Notification
                        content={
                            status === true
                                ? 'Dealer successfully moved.'
                                : 'The dealer number you are migrating to is either in the same dealer group or cannot be found.'
                        }
                        type={
                            status === true
                                ? NotificationType.SUCCESS
                                : NotificationType.ERROR
                        }
                        visibility={status !== null}
                        setVisibility={() => setStatus(null)}
                    />
                ) : null}

                <div className='page-title'>
                    <h5>Move dealer to another group</h5>
                </div>
                <hr />

                <p className='move-dealer__description'>
                    Enter a valid Dealer Number that belongs to the Dealer Group
                    that you wish to move Dealer Number {state?.dealerIdToMove}{' '}
                    and its users to. Check Preserve Permissions if you wish to
                    keep the user(s) current permissions intact (excluding the
                    UA permission).
                </p>
                <form
                    className='o-layout__item u-1/1 u-1/1@xs'
                    onSubmit={handleSubmit}
                >
                    <div className='o-fieldset__row u-mt-small'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@s'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        Enter Dealer Number *
                                        <TooltipIcon text='Dealer number must have a valid format and be active in dSource.' />
                                    </label>

                                    <div className='c-input u-mt-small'>
                                        <div className='input-text-wrapper'>
                                            <input
                                                className='c-input__input'
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>,
                                                ) => {
                                                    setFieldValue(
                                                        'targetDealerId',
                                                        e.target.value.toUpperCase(),
                                                    );
                                                }}
                                                onBlur={handleBlur}
                                                type='text'
                                                value={values.targetDealerId}
                                                disabled={isLoading}
                                                name='targetDealerId'
                                                maxLength={6}
                                                data-testid={'targetDealerId'}
                                            />
                                            {touched.targetDealerId &&
                                                !isSubmitting &&
                                                errors.targetDealerId && (
                                                    <>
                                                        <br />
                                                        <div className='error-message'>
                                                            {
                                                                errors.targetDealerId
                                                            }
                                                        </div>
                                                    </>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='o-fieldset__row u-mt-small'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@s'>
                                <Checkbox
                                    name={'Preserve Permissions'}
                                    value={'preserve'}
                                    onCheck={onPreserveCheck}
                                    checked={values.preserve === true}
                                    allBlack={true}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='o-fieldset__row row_button-group'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@s'>
                                <div className='o-fieldset__row button-group'>
                                    <button
                                        className={
                                            'c-btn c-btn--secondary u-mr-small'
                                        }
                                        type='button'
                                        onClick={confirmHandler}
                                    >
                                        <span className='c-btn__text'>
                                            Cancel
                                        </span>
                                    </button>

                                    <button
                                        className={'c-btn move-btn'}
                                        type='submit'
                                        disabled={!(isValid && dirty)}
                                    >
                                        <span className='c-btn__text'>
                                            {isLoading ? (
                                                <Spinner />
                                            ) : (
                                                'Move dealer'
                                            )}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DealerGroupMove;
